import {createApi} from '@reduxjs/toolkit/query/react'
import {baseQuery} from '../base-query'

import type {
  IAddGeneralAdminRequest,
  IPaginationArgs,
  ISuccessMessage,
  IUserBase,
  IUserAndEmail,
  TAddMemberPayload,
  TEditMemberPayload,
  TOnboardPayload,
  TUpdateUserPayload,
  TUsersPayload,
} from '../../models'

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: baseQuery,
  tagTypes: ['User'],
  endpoints: (builder) => ({
    getUsersByCompanyId: builder.query<IUserBase[], string>({
      providesTags: ['User'],
      query: (companyId) => ({
        url: `/User/UsersByCompanyId/${companyId}`,
        method: 'GET',
      }),
    }),

    getUsersByCompanyIdPagination: builder.query<
      TUsersPayload,
      IPaginationArgs & {
        companyId: string
        firstName?: string
        lastName?: string
        emailAddress?: string
        filterRole?: string
        status?: string
        sortBy?: string
        sortOrder?: 'asc' | 'desc'
      }
    >({
      providesTags: ['User'],
      query: ({
        pageNumber,
        pageSize,
        companyId,
        firstName,
        lastName,
        emailAddress,
        filterRole,
        status,
        sortBy,
        sortOrder,
      }) => ({
        url: `/user/getUsersByCompanyIdPagination`,
        method: 'GET',
        params: {
          pageNumber,
          pageSize,
          companyId,
          ...(firstName && {firstName}),
          ...(lastName && {lastName}),
          ...(emailAddress && {emailAddress}),
          ...(filterRole && {filterRole}),
          ...(status && {status}),
          ...(sortBy && {sortBy}),
          ...(sortOrder && {sortOrder}),
        },
      }),
    }),

    getUsersByAgencyIdPagination: builder.query<
      TUsersPayload,
      IPaginationArgs & {
        agencyId: string
        firstName?: string
        lastName?: string
        emailAddress?: string
        filterRole?: string
        status?: string
        sortBy?: string
        sortOrder?: 'asc' | 'desc'
      }
    >({
      providesTags: ['User'],
      query: ({
        pageNumber,
        pageSize,
        agencyId,
        firstName,
        lastName,
        emailAddress,
        filterRole,
        status,
        sortBy,
        sortOrder,
      }) => ({
        url: `/user/getUsersByAgencyIdPagination`,
        method: 'GET',
        params: {
          pageNumber,
          pageSize,
          agencyId,
          ...(firstName && {firstName}),
          ...(lastName && {lastName}),
          ...(emailAddress && {emailAddress}),
          ...(filterRole && {filterRole}),
          ...(status && {status}),
          ...(sortBy && {sortBy}),
          ...(sortOrder && {sortOrder}),
        },
      }),
    }),

    getAllUsersPagination: builder.query<TUsersPayload, IPaginationArgs>({
      providesTags: ['User'],
      query: ({pageNumber, pageSize}) => ({
        url: `/user/getAllUsersPagination`,
        method: 'GET',
        params: {pageNumber, pageSize},
      }),
    }),

    getUsersSortedByName: builder.query<
      TUsersPayload,
      IPaginationArgs & {
        role?: string
        matchingRoles?: boolean
        agencyId?: string
        firstName?: string
        lastName?: string
        emailAddress?: string
        filterRole?: string
        status?: 'active' | 'disabled' | 'pending'
        sortBy?: 'firstName' | 'lastName' | 'emailAddress' | 'role' | 'status' | 'modifiedDate'
        sortOrder?: 'asc' | 'desc'
        companyId?: string
      }
    >({
      providesTags: ['User'],
      query: ({
        pageNumber,
        pageSize,
        role,
        matchingRoles,
        agencyId,
        firstName,
        lastName,
        emailAddress,
        filterRole,
        status,
        sortBy = 'firstName',
        sortOrder = 'asc',
        companyId,
      }) => {
        // Build params object, excluding undefined/empty values
        const params: Record<string, string | number | boolean> = {
          pageNumber,
          pageSize,
          sortBy,
          sortOrder,
          application: 'MSP',
        }
        // Only include optional params if they have values
        if (role) params.role = role
        if (matchingRoles !== undefined) params.matchingRoles = matchingRoles
        if (agencyId) params.agencyId = agencyId
        if (firstName) params.firstName = firstName
        if (lastName) params.lastName = lastName
        if (emailAddress) params.emailAddress = emailAddress
        if (filterRole) params.filterRole = filterRole
        if (status) params.status = status
        if (companyId) params.companyId = companyId

        return {
          url: `/User`,
          method: 'GET',
          params,
        }
      },
      // TODO: instead of doing this just add a temporary full name prop to the user.
      transformResponse: ({users, totalUsers}) => {
        return {
          totalUsers: totalUsers,
          users: users.sort((a: IUserBase, b: IUserBase) =>
            `${a.firstName} ${a.lastName}` > `${b.firstName} ${b.lastName}` ? 1 : -1,
          ),
        }
      },
    }),

    getUserByEmail: builder.query<IUserBase | undefined, string>({
      providesTags: ['User'],
      query: (email) => ({
        url: `/user/userByEmail/${email}`,
        method: 'GET',
      }),
    }),

    /**
     * Get the current user's profile based on the JWT token.
     * This endpoint bypasses authorization to allow users to fetch their own profile
     * during login flow, regardless of their role's permissions.
     */
    getCurrentUser: builder.query<IUserBase, void>({
      providesTags: ['User'],
      query: () => ({
        url: `/user/me`,
        method: 'GET',
      }),
    }),

    getUsersByEmails: builder.query<IUserAndEmail[], string[]>({
      providesTags: ['User'],
      query: (emails) => ({
        url: `/user/usersByEmails`,
        method: 'POST',
        body: emails,
      }),
    }),

    // New consolidated onboarding endpoint
    onboardUser: builder.mutation<ISuccessMessage, TOnboardPayload>({
      invalidatesTags: ['User'],
      query: (body) => ({
        url: `/user/onboardUser`,
        method: 'POST',
        body: body,
      }),
    }),

    // Legacy createUser endpoint using the new consolidated endpoint
    createUser: builder.mutation<ISuccessMessage, TOnboardPayload>({
      invalidatesTags: ['User'],
      query: (body) => ({
        url: `/user/onboardUser`,
        method: 'POST',
        body: body,
      }),
    }),

    // Updated specific onboarding endpoints using the consolidated endpoint
    addPlatformAdmin: builder.mutation<ISuccessMessage, TAddMemberPayload>({
      invalidatesTags: ['User'],
      query: (body) => ({
        url: `/user/onboardUser`,
        method: 'POST',
        body: {
          ...body,
          roles: ['platformadmin'],
        },
      }),
    }),

    addCompanyAdmin: builder.mutation<ISuccessMessage, TAddMemberPayload>({
      invalidatesTags: ['User'],
      query: (body) => ({
        url: `/user/onboardUser`,
        method: 'POST',
        body: {
          ...body,
          roles: ['companyadmin'],
        },
      }),
    }),

    addAgencyAdmin: builder.mutation<ISuccessMessage, TAddMemberPayload>({
      invalidatesTags: ['User'],
      query: (body) => ({
        url: `/user/onboardUser`,
        method: 'POST',
        body: {
          ...body,
          roles: ['agencyadmin'],
        },
      }),
    }),

    addGeneralAdmin: builder.mutation<IUserBase, IAddGeneralAdminRequest>({
      invalidatesTags: ['User'],
      query: (body) => ({
        url: `/user/onboardUser`,
        method: 'POST',
        body: {
          ...body,
          roles: ['general_admin'],
        },
      }),
    }),

    updateMember: builder.mutation<ISuccessMessage, TEditMemberPayload>({
      invalidatesTags: ['User'],
      query: (body) => ({
        url: `/User`,
        method: 'PUT',
        body: body,
      }),
    }),

    updateUser: builder.mutation<ISuccessMessage, TUpdateUserPayload>({
      invalidatesTags: ['User'],
      query: (body) => ({
        url: `/User`,
        method: 'PUT',
        body: body,
      }),
    }),

    updateUserStatus: builder.mutation<IUserBase, {id: string; status: string}>({
      invalidatesTags: ['User'],
      query: ({id, status}) => ({
        url: `/user/updateUserStatus/${id}?status=${status}`,
        method: 'PUT',
      }),
    }),

    deleteUser: builder.mutation<number, string>({
      invalidatesTags: ['User'],
      query: (id) => ({
        url: `/user/${id}`,
        method: 'DELETE',
      }),
    }),

    deleteUserByEmail: builder.mutation<number, string>({
      invalidatesTags: ['User'],
      query: (email) => ({
        url: `/user/deleteUserByEmail/${email}`,
        method: 'DELETE',
      }),
    }),
  }),
})

export const {
  useCreateUserMutation,
  useOnboardUserMutation,
  useGetUsersByCompanyIdPaginationQuery,
  useGetUsersByAgencyIdPaginationQuery,
  useGetAllUsersPaginationQuery,
  useLazyGetUsersByCompanyIdPaginationQuery,
  useLazyGetUsersByAgencyIdPaginationQuery,
  useLazyGetAllUsersPaginationQuery,
  useGetUsersByCompanyIdQuery,
  useGetUsersSortedByNameQuery,
  useGetUsersByEmailsQuery,
  useLazyGetUserByEmailQuery,
  useLazyGetCurrentUserQuery,
  useLazyGetUsersByCompanyIdQuery,
  useUpdateUserMutation,
  useAddPlatformAdminMutation,
  useAddGeneralAdminMutation,
  useAddCompanyAdminMutation,
  useAddAgencyAdminMutation,
  useUpdateMemberMutation,
  useUpdateUserStatusMutation,
  useDeleteUserMutation,
  useDeleteUserByEmailMutation,
} = userApi
